import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Student, Teacher, ClassSetting, ClassRole } from '../../../utils/firebase';
import { doc, updateDoc, addDoc, collection, query, where, getDocs, deleteDoc, getDoc, onSnapshot, setDoc, writeBatch, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, COLLECTIONS, getClassPointRankingMode, getTotalEarnedPointsByStudent, upsertUsersRootProfile } from '../../../utils/firebase';
import { createPointNotification } from '../../../utils/notifications';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import StudentApprovalPanel from '../../../components/StudentApprovalPanel';
import SearchIcon from '../../../components/icons/SearchIcon';
import LeftArrowIcon from '../../../components/icons/LeftArrowIcon';
import RightArrowIcon from '../../../components/icons/RightArrowIcon';
import LeftArrowDoubleIcon from '../../../components/icons/LeftArrowDoubleIcon';
import RightArrowDoubleIcon from '../../../components/icons/RightArrowDoubleIcon';
import TrashIcon from '../../../components/icons/TrashIcon';
import SettingIcon from '../../../components/icons/SettingIcon';
import EditIcon from '../../../components/icons/EditIcon';
import CloseCircleIcon from '../../../components/icons/CloseCircleIcon';
import PlusCircleIcon from '../../../components/icons/PlusCircleIcon';
import EditStudentInfoDialog from '../../../components/ui/EditStudentInfoDialog';
import type { CharacterStats } from '../../../types/character';
import StudentPointEditDialog from '../../../components/ui/StudentPointEditDialog';
import StudentRoleAssignmentDialog from '../../../components/ui/StudentRoleAssignmentDialog';
import StudentInfoDialog from '../../../components/ui/StudentInfoDialog';
import ClassCodeDialog from '../../../components/ui/ClassCodeDialog';

interface FormData {
  name: string;
  email: string;
  password: string;
  showPassword: boolean;
  grade: number;
  class: number;
  number: number;
  loginType?: 'local' | 'social';
  username?: string;
  points: number;
}

interface RoleInfo {
  id: string;
  name: string;
  description: string;
  salaryType: string;
  salaryPeriod: string;
  salary: number;
  isActive: boolean;
  maxCount: number;
  targetDate: Date;
}

interface PointHistory {
  id: string;
  studentId: string;
  studentName: string;
  amount: number;
  type: 'add' | 'subtract' | 'use' | 'oneTime';
  reason: string;
  createdAt: Date;
  teacherId: string;
  teacherName: string;
  balance?: number;
}

const MAX_ROLES_PER_STUDENT = 1;

const getStudentAssignedRoles = (student: Pick<Student, 'assignedRoles'>) =>
  (Array.isArray(student.assignedRoles) ? student.assignedRoles : []).filter(
    (role): role is NonNullable<Student['assignedRoles']>[number] => Boolean(role?.roleId)
  );

const ClassManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const teacher = currentUser as Teacher;

  const [students, setStudents] = useState<Student[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [classSettings, setClassSettings] = useState<ClassSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<ClassRole[]>([]);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    showPassword: false,
    grade: 0,
    class: 0,
    number: 0,
    points: 0
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);
  const [deleteType, setDeleteType] = useState<'remove' | 'delete'>('remove'); // 'remove': 학급에서만 제거, 'delete': 완전 삭제
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set()); // 일괄 삭제를 위한 선택된 학생 ID 목록
  const [openBatchDeleteDialog, setOpenBatchDeleteDialog] = useState(false);
  /** 목록 로딩(loading)과 분리 — 일괄 삭제 중 onSnapshot이 loading을 건드리면 버튼/다이얼로그가 꼬임 */
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [batchDeleteType, setBatchDeleteType] = useState<'remove' | 'delete'>('remove');
  const [success, setSuccess] = useState<string | null>(null);
  const [openClassCodeDialog, setOpenClassCodeDialog] = useState(false);
  const [classCode, setClassCode] = useState<string | null>(null);
  const [classInfo, setClassInfo] = useState<any>(null);
  const [openPointHistoryDialog, setOpenPointHistoryDialog] = useState(false);
  const [pointHistory, setPointHistory] = useState<PointHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [openPointEditDialog, setOpenPointEditDialog] = useState(false);
  const [pointEditStudent, setPointEditStudent] = useState<Student | null>(null);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [infoStudent, setInfoStudent] = useState<Student | null>(null);
  
  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // 승인 대기 학생 목록 (실제로는 StudentApprovalPanel에서 가져와야 함)
  const [pendingStudents, setPendingStudents] = useState<Student[]>([]);
  
  // 정렬 상태
  const [sortBy, setSortBy] = useState<'name' | 'points' | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // 포인트 등수 기준 (담임 설정과 동일하게 정렬 시 반영)
  const [pointRankingMode, setPointRankingMode] = useState<'balance' | 'totalEarned'>('balance');
  const [totalEarnedByStudent, setTotalEarnedByStudent] = useState<Record<string, number>>({});
  
  // 모바일 감지 상태
  const [isMobile, setIsMobile] = useState(false);

  // 모바일 감지 useEffect
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 포인트 등수 기준 및 총 벌어든 포인트(학생별) 로드 (포인트 정렬 시 반영)
  useEffect(() => {
    const schoolId = teacher?.schoolId;
    const classId = teacher?.classId;
    if (!schoolId || !classId) return;
    let cancelled = false;
    (async () => {
      try {
        const mode = await getClassPointRankingMode(schoolId, classId);
        if (cancelled) return;
        setPointRankingMode(mode);
        if (mode === 'totalEarned') {
          const byStudent = await getTotalEarnedPointsByStudent(schoolId, classId);
          if (!cancelled) setTotalEarnedByStudent(byStudent);
        } else {
          setTotalEarnedByStudent({});
        }
      } catch {
        setPointRankingMode('balance');
        setTotalEarnedByStudent({});
      }
    })();
    return () => { cancelled = true; };
  }, [teacher?.schoolId, teacher?.classId]);

  // 교사의 homeroomClass 정보와 학생 목록 가져오기
  useEffect(() => {
    if (!teacher?.uid) return;

    try {
      setLoading(true);
      // 교사 정보 실시간 감시
      const teacherRef = doc(db, `schools/${teacher.schoolId}/teachers/${teacher.uid}`);
      const unsubscribeTeacher = onSnapshot(teacherRef, async (teacherDoc) => {
        if (teacherDoc.exists()) {
          const data = teacherDoc.data();
          // 학급 정보 설정
          if (data.homeroomClass) {
            setClassInfo({
              id: `${teacher.schoolId}_${data.homeroomClass.grade}_${data.homeroomClass.class}`,
              ...data.homeroomClass
            });
          }
          // 학급 역할 정보 가져오기
          if (data.homeroomClass) {
            const classId = `${teacher.schoolId}_${data.homeroomClass.grade}_${data.homeroomClass.class}`;
            console.log('Loading roles for classId:', classId);
            
            const rolesRef = collection(db, `schools/${teacher.schoolId}/classes/${classId}/classRoles`);
            const rolesSnapshot = await getDocs(rolesRef);
            console.log('Roles snapshot size:', rolesSnapshot.size);
            
            const rolesData = rolesSnapshot.docs.map(docSnap => {
              const roleData = docSnap.data();
              const roleId =
                typeof roleData.id === 'string' && roleData.id ? roleData.id : docSnap.id;
              console.log('Role data:', roleData);
              return {
                ...roleData,
                schoolId: teacher.schoolId,
                classId: classId,
                id: roleId
              };
            }) as ClassRole[];
            
            console.log('Processed roles data:', rolesData);
            setAvailableRoles(rolesData);
          }
        }
      });

      // 학생 목록 실시간 감시 (승인된 학생)
      if (teacher.homeroomClass) {
        console.log('Fetching students for:', teacher.homeroomClass);
        const studentsRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students`);
        const q = query(studentsRef);
        
        console.log('Query parameters:', {
          schoolId: teacher.schoolId,
          classId: teacher.classId
        });

        const unsubscribeStudents = onSnapshot(q, (snapshot) => {
          console.log('Query snapshot size:', snapshot.size);
          const studentsData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              ...data,
              uid: doc.id,
              loginType: data.loginType === 'social' ? doc.id : undefined
            } as Student;
          });
          
          console.log('Fetched students:', studentsData);
          setStudents(studentsData || []);
          // 초기 로딩 해제만 — 이후 스냅샷마다 false로 두면 일괄 삭제 중 setLoading(true)가 무효화됨
          setLoading((prev) => (prev ? false : prev));
        });

        // 승인 대기 학생 목록 실시간 감시
        // student_requests 컬렉션에서 해당 학급의 승인 대기 학생 가져오기
        const studentRequestsRef = collection(db, COLLECTIONS.STUDENT_REQUESTS);
        const studentRequestsQuery = query(
          studentRequestsRef,
          where('status', '==', 'pending'),
          where('schoolId', '==', teacher.schoolId)
        );
        
        const unsubscribePending = onSnapshot(studentRequestsQuery, (snapshot) => {
          const pendingData = snapshot.docs
            .map(doc => {
              const data = doc.data();
              return {
                ...data,
                uid: doc.id
              } as Student;
            })
            // 클라이언트에서 해당 학급의 학생만 필터링
            .filter(student => {
              if (!teacher.homeroomClass) return false;
              // classId가 일치하는 경우
              if (student.classId === teacher.classId) return true;
              // grade와 class가 일치하는 경우
              if (student.grade === teacher.homeroomClass.grade && 
                  student.class === teacher.homeroomClass.class) return true;
              return false;
            });
          
          setPendingStudents(pendingData || []);
        }, (error) => {
          console.error('승인 대기 학생 목록 로드 중 오류:', error);
          setPendingStudents([]);
        });

        return () => {
          unsubscribeTeacher();
          unsubscribeStudents();
          unsubscribePending();
        };
      } else {
        setStudents([]);
        setPendingStudents([]);
        setLoading(false);
      }
    } catch (err) {
      console.error('데이터 로딩 중 오류:', err);
      setError('데이터를 불러오는데 실패했습니다.');
      setStudents([]);
      setPendingStudents([]);
      setLoading(false);
    }
  }, [teacher?.uid, teacher.homeroomClass]);

  // 선택된 학급의 학생들만 필터링 및 정렬
  const filteredStudents = useMemo(() => {
    if (!teacher.homeroomClass) return [];
    
    let filtered = students.filter(student => {
        const matchesSearch = searchQuery
          ? student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            student.email.toLowerCase().includes(searchQuery.toLowerCase())
          : true;

        return matchesSearch;
    });

    // 정렬 적용
    if (sortBy === 'name') {
      filtered.sort((a, b) => {
        const comparison = a.name.localeCompare(b.name, 'ko');
        return sortOrder === 'asc' ? comparison : -comparison;
      });
    } else if (sortBy === 'points') {
      const rankPoints = (s: Student) =>
        pointRankingMode === 'totalEarned'
          ? (totalEarnedByStudent[s.uid] ?? 0)
          : (s.points ?? 0);
      filtered.sort((a, b) => {
        const comparison = rankPoints(a) - rankPoints(b);
        return sortOrder === 'asc' ? comparison : -comparison;
      });
    } else {
      // 기본 정렬: 번호순
      filtered.sort((a, b) => {
        const numA = Number(a.number || '0');
        const numB = Number(b.number || '0');
        return numA - numB;
      });
    }

    return filtered;
  }, [students, searchQuery, teacher.homeroomClass, sortBy, sortOrder, pointRankingMode, totalEarnedByStudent]);

  // 페이지네이션 계산 (데스크톱만)
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentStudents = isMobile ? filteredStudents : filteredStudents.slice(startIndex, endIndex);

  // 페이지네이션 함수
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // 정렬 토글 함수
  const handleSort = (field: 'name' | 'points') => {
    if (sortBy === field) {
      // 같은 필드 클릭 시 오름차순/내림차순 토글
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // 다른 필드 클릭 시 해당 필드로 오름차순 정렬
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleOpenDialog = (student?: Student) => {
    if (student) {
      setSelectedStudent(student);
      setFormData({
        name: student.name,
        email: student.email,
        loginType: student.loginType || 'local',
        username: student.username || '',
        password: student.password || '',
        showPassword: false,
        grade: student.grade || 0,
        class: student.class || 0,
        number: student.number || 0,
        points: student.points || 0
      });
    } else {
      setSelectedStudent(null);
      setFormData({
        name: '',
        email: '',
        loginType: 'local',
        username: '',
        password: '',
        showPassword: false,
        grade: teacher.homeroomClass?.grade || 0,
        class: teacher.homeroomClass?.class || 0,
        number: 0,
        points: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedStudent(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teacher) return;

    try {
      const studentData = {
        name: formData.name,
        grade: Number(formData.grade),
        class: Number(formData.class),
        number: Number(formData.number),
        points: Number(formData.points),
        assignedRoles: [],
        roles: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (selectedStudent) {
        // 기존 학생 정보 수정
        // 학급 ID 생성 (예: "1-1" 형식)
        const classId = teacher.classId;
        const studentRef = doc(db, `schools/${teacher.schoolId}/classes/${classId}/students`, selectedStudent.uid);
        
        // 문서가 존재하는지 확인
        const studentDoc = await getDoc(studentRef);
        if (!studentDoc.exists()) {
          // 문서가 없으면 새로 생성
          await setDoc(studentRef, {
            ...studentData,
            uid: selectedStudent.uid,
            email: selectedStudent.email,
            loginType: selectedStudent.loginType,
            status: 'active'
          });
        } else {
          // 문서가 있으면 업데이트
          const updateData: any = {
            name: studentData.name,
            number: Number(formData.number),
            points: Number(formData.points),
            updatedAt: new Date()
          };
          await updateDoc(studentRef, updateData);
        }
        
        setSuccess('학생 정보가 수정되었습니다.');
      } 
      handleCloseDialog();
    } catch (err) {
      console.error('학생 정보 저장 중 오류 발생:', err);
      setError('학생 정보를 저장하는데 실패했습니다.');
    }
  };

  const handleDelete = async (student: Student) => {
    setStudentToDelete(student);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!studentToDelete) return;

    try {
      setLoading(true);
      setError(null);
      console.log('삭제할 학생:', studentToDelete);
      console.log('삭제 타입:', deleteType);
      
      const studentId = studentToDelete.uid;
      
      // 완전 삭제인 경우 Cloud Functions 사용 (모든 데이터 삭제)
      if (deleteType === 'delete') {
        try {
          const functions = getFunctions();
          const deleteStudentAccount = httpsCallable(functions, 'deleteStudentAccount');
          await deleteStudentAccount({
            studentId: studentId,
            schoolId: teacher.schoolId,
            classId: teacher.classId
          });
          console.log('Firebase Auth 계정 삭제 완료');
          
          // 로컬 상태 업데이트
          setStudents(prev => prev.filter(s => s.uid !== studentId));
          
          setSuccess(`${studentToDelete.name} 학생이 완전히 탈퇴되었습니다.`);
          setOpenDeleteDialog(false);
          setStudentToDelete(null);
          setDeleteType('remove');
          return;
        } catch (authError: any) {
          console.error('Firebase Auth 계정 삭제 중 오류:', authError);
          setError('계정 삭제 중 오류가 발생했습니다.');
          setOpenDeleteDialog(false);
          setStudentToDelete(null);
          setDeleteType('remove');
          return;
        }
      }
      
      // 학급에서만 제거하는 경우 (로컬 Firestore 작업)
      const batch = writeBatch(db);
      
      // 1. 학생 문서 삭제
      const studentRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students`, studentId);
      batch.delete(studentRef);
      
      // 2. pendingStudents에서도 삭제 (혹시 있을 경우)
      const pendingRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pendingStudents`, studentId);
      const pendingDoc = await getDoc(pendingRef);
      if (pendingDoc.exists()) {
        batch.delete(pendingRef);
      }
      
      // 3. 포인트 승인 요청 삭제
      const approvalsRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointApprovals`);
      const approvalsQuery = query(approvalsRef, where('uid', '==', studentId));
      const approvalsSnapshot = await getDocs(approvalsQuery);
      approvalsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // 4. 배치 작업 실행
      await batch.commit();
      console.log('학급에서 학생 제거 완료');
      
      // 로컬 상태 업데이트
      setStudents(prev => prev.filter(s => s.uid !== studentId));
      
      setSuccess(`${studentToDelete.name} 학생이 이 학급에서 제거되었습니다.`);
      setOpenDeleteDialog(false);
      setStudentToDelete(null);
      setDeleteType('remove');
    } catch (err) {
      console.error('학생 삭제 중 오류:', err);
      setError('학생 처리 중 오류가 발생했습니다.');
      // 에러 발생 시에도 다이얼로그 닫기
      setOpenDeleteDialog(false);
      setStudentToDelete(null);
      setDeleteType('remove');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setStudentToDelete(null);
    setDeleteType('remove'); // 기본값으로 리셋
  };

  // 학생 선택 토글
  const handleSelectStudent = (studentId: string) => {
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  // 전체 선택/해제
  const handleSelectAll = () => {
    if (selectedStudents.size === filteredStudents.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(filteredStudents.map(s => s.uid)));
    }
  };

  // 일괄 삭제 핸들러
  const handleBatchDelete = () => {
    if (selectedStudents.size === 0) return;
    setOpenBatchDeleteDialog(true);
  };

  const handleConfirmBatchDelete = async () => {
    if (selectedStudents.size === 0) return;

    try {
      setBatchDeleting(true);
      setError(null);
      
      const studentsToDelete = filteredStudents.filter(s => selectedStudents.has(s.uid));
      let successCount = 0;
      let failCount = 0;
      const failedStudents: string[] = [];

      for (const student of studentsToDelete) {
        try {
          const batch = writeBatch(db);
          const studentId = student.uid;
          
          // 1. 학생 문서 삭제
          const studentRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students`, studentId);
          batch.delete(studentRef);
          
          // 2. pendingStudents에서도 삭제
          const pendingRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pendingStudents`, studentId);
          const pendingDoc = await getDoc(pendingRef);
          if (pendingDoc.exists()) {
            batch.delete(pendingRef);
          }
          
          // 3. 포인트 승인 요청 삭제
          const approvalsRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointApprovals`);
          const approvalsQuery = query(approvalsRef, where('uid', '==', studentId));
          const approvalsSnapshot = await getDocs(approvalsQuery);
          approvalsSnapshot.docs.forEach(doc => {
            batch.delete(doc.ref);
          });
          
          // 4. 배치 작업 실행
          await batch.commit();
          
          // 5. 완전 삭제인 경우 Firebase Auth도 삭제
          if (batchDeleteType === 'delete') {
            try {
              const functions = getFunctions();
              const deleteStudentAccount = httpsCallable(functions, 'deleteStudentAccount');
              await deleteStudentAccount({
                studentId: studentId,
                schoolId: teacher.schoolId,
                classId: teacher.classId
              });
            } catch (authError: any) {
              console.error(`Firebase Auth 계정 삭제 중 오류 (${student.name}):`, authError);
              // Auth 삭제 실패해도 Firestore 삭제는 완료되었으므로 계속 진행
            }
          }
          
          successCount++;
        } catch (err) {
          console.error(`학생 삭제 중 오류 (${student.name}):`, err);
          failCount++;
          failedStudents.push(student.name);
        }
      }
      
      // 로컬 상태 업데이트
      setStudents(prev => prev.filter(s => !selectedStudents.has(s.uid)));
      setSelectedStudents(new Set());
      setOpenBatchDeleteDialog(false);
      setBatchDeleteType('remove');
      
      if (failCount === 0) {
        setSuccess(`${successCount}명의 학생이 ${batchDeleteType === 'delete' ? '완전히 삭제' : '제거'}되었습니다.`);
      } else {
        setSuccess(`${successCount}명의 학생이 ${batchDeleteType === 'delete' ? '완전히 삭제' : '제거'}되었습니다.`);
        setError(`${failCount}명의 학생 처리에 실패했습니다: ${failedStudents.join(', ')}`);
      }
    } catch (err) {
      console.error('일괄 삭제 중 오류:', err);
      setError('일괄 삭제 처리 중 오류가 발생했습니다.');
    } finally {
      setBatchDeleting(false);
    }
  };

  const handleCancelBatchDelete = () => {
    if (batchDeleting) return;
    setOpenBatchDeleteDialog(false);
    setBatchDeleteType('remove');
  };

  const handlePointChange = async (studentId: string, points: number) => {
    try {
      const studentRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students`, studentId);
      await updateDoc(studentRef, {
        points: Math.max(0, points),
        updatedAt: new Date()
      });
    } catch (err) {
      console.error('포인트 업데이트 중 오류:', err);
      setError('포인트를 업데이트하는데 실패했습니다.');
      return;
    }
    setStudents((prevStudents = []) => prevStudents.map(student =>
      student.uid === studentId
        ? {
            ...student,
            points: Math.max(0, points),
            updatedAt: new Date()
          }
        : student
    ));
  };

  const handleOpenRoleDialog = (student: Student) => {
    setSelectedStudent(student);
    setOpenRoleDialog(true);
  };

  const handleCloseRoleDialog = () => {
    setOpenRoleDialog(false);
    setSelectedStudent(null);
  };

  // 역할 할당
  const handleAssignRole = async (roleId: string) => {
    if (!selectedStudent) return;
    
    const role = availableRoles.find(r => r.id === roleId);
    if (!role) return;
    
    await handleRoleChange(selectedStudent.uid, role);
  };

  const findClassRole = (roleId?: string) =>
    availableRoles.find(role => role.id === roleId);

  const applyAssignedRoles = (
    studentId: string,
    assignedRoles: NonNullable<Student['assignedRoles']>
  ) => {
    setStudents(prev =>
      prev.map(s => (s.uid === studentId ? { ...s, assignedRoles } : s))
    );
    setSelectedStudent(prev =>
      prev?.uid === studentId ? { ...prev, assignedRoles } : prev
    );
  };

  const handleRoleChange = async (studentId: string, role: ClassRole) => {
    try {
      // 학생 문서 업데이트
      const studentRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students/${studentId}`);
      const studentDoc = await getDoc(studentRef);
      const knownRoleIds = new Set(availableRoles.map(r => r.id));
      const currentRoles = getStudentAssignedRoles({
        assignedRoles: studentDoc.data()?.assignedRoles
      }).filter(r => knownRoleIds.has(r.roleId));
      
      // 이미 해당 역할이 있는지 확인
      if (currentRoles.some((r: { roleId: string }) => r.roleId === role.id)) {
        setError('이미 해당 역할이 할당되어 있습니다.');
        return;
      }
      
      // 새로운 역할 추가
      const newRole = {
        roleId: role.id,
        assignedAt: new Date(),
        isActive: true
      };
      
      await updateDoc(studentRef, {
        assignedRoles: [...currentRoles, newRole]
      });
      
      // 포인트 승인 요청 생성
      const now = new Date();
      let targetDate = new Date(now);
      
      // salaryPeriod에 따라 targetDate 설정
      if (role.salaryPeriod === 'daily') {
        targetDate.setHours(23, 59, 0, 0);
      } else if (role.salaryPeriod === 'weekly') {
        // 다음 일요일 23:59로 설정
        const daysUntilSunday = 7 - now.getDay();
        targetDate.setDate(now.getDate() + daysUntilSunday);
        targetDate.setHours(23, 59, 0, 0);
      } else if (role.salaryPeriod === 'monthly') {
        // 이번 달 마지막 날 23:59로 설정
        targetDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        targetDate.setHours(23, 59, 0, 0);
      }

      // 승인 요청 생성
      const approvalRef = doc(collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointApprovals`));
      await setDoc(approvalRef, {
        uid: studentId,
        roleId: role.id,
        salaryType: role.salaryType,
        salaryPeriod: role.salaryPeriod,
        targetDate: targetDate,
        isApproved: false,
        pointAutoPay: role.pointAutoPay,
        pointManagerApproved: null,
        teacherApproved: null,
        pointManagerApprovedAt: null,
        teacherApprovedAt: null,
        createdAt: new Date(),
        updatedAt: new Date(),
        schoolId: teacher.schoolId,
        classId: teacher.classId
      });
      
      applyAssignedRoles(studentId, [...currentRoles.filter(r => r.roleId !== role.id), newRole]);
      
      setError(null);
      handleCloseRoleDialog();
    } catch (error) {
      console.error('역할 변경 중 오류:', error);
      setError('역할 변경에 실패했습니다.');
    }
  };

  const handleRemoveRole = async (studentId: string, roleId: string) => {
    try {
      const studentRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students/${studentId}`);
      const studentDoc = await getDoc(studentRef);

      if (!studentDoc.exists()) {
        setError('학생 정보를 찾을 수 없습니다.');
        return;
      }

      const knownRoleIds = new Set(availableRoles.map(r => r.id));
      const currentRoles = getStudentAssignedRoles({
        assignedRoles: studentDoc.data()?.assignedRoles
      });
      const updatedRoles = currentRoles.filter((r: { roleId: string }) => {
        if (r.roleId === roleId) return false;
        if (knownRoleIds.size === 0) return true;
        return knownRoleIds.has(r.roleId);
      });

      // 승인이 필요한 역할인 경우 포인트 승인 요청 삭제
      const approvalsRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointApprovals`);
      const q = query(
        approvalsRef,
        where('uid', '==', studentId),
        where('roleId', '==', roleId)
      );
      const querySnapshot = await getDocs(q);
      
      const batch = writeBatch(db);
      querySnapshot.docs.forEach(approvalDoc => {
        batch.delete(approvalDoc.ref);
      });
      batch.update(studentRef, {
        assignedRoles: updatedRoles
      });
      await batch.commit();

      applyAssignedRoles(studentId, updatedRoles);
      
      setError(null);
    } catch (err) {
      console.error('역할 제거 중 오류:', err);
      setError('역할 제거에 실패했습니다.');
    }
  };

  // 학급 코드 생성
  const generateClassCode = async () => {
    if (!teacher.homeroomClass) return;
    
    try {
      setError(null);
      setLoading(true);
      
      //학급 코드가 이미 있는지 확인
      const classCodeRef = collection(db, COLLECTIONS.CLASS_CODES);
      const q = query(
        classCodeRef,
        where('schoolId', '==', teacher.schoolId),
        where('classId', '==', teacher.classId),
        where('grade', '==', teacher.homeroomClass?.grade),
        where('class', '==', teacher.homeroomClass?.class)
      );
      const querySnapshot = await getDocs(q);

      //기존 학급 코드가 있으면 재사용, 없으면 새로 생성
      if (!querySnapshot.empty) {
        const existingCode = querySnapshot.docs[0].data().code;
        setClassCode(existingCode);
        setOpenClassCodeDialog(true);
        return;
      } else{
        // 6자리 랜덤 코드 생성
        const code = Math.random().toString(36).substring(2, 8).toUpperCase();

        // 학급 코드 문서 생성 및 추가 
        await addDoc(collection(db, `classCodes`), {
          code,
          schoolId: teacher.schoolId,
          grade: teacher.homeroomClass.grade,
          class: teacher.homeroomClass.class,
          teacherId: teacher.uid,
          classId: teacher.classId,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        setClassCode(code);
        setOpenClassCodeDialog(true);
      }
    } catch (err) {
      console.error('학급 코드 생성 중 오류:', err);
      setError('학급 코드 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseClassCodeDialog = () => {
    setOpenClassCodeDialog(false);
    setClassCode(null);
  };

  const getAvailableRoles = () => {
    console.log('Available roles before filtering:', availableRoles);
    console.log('Students:', students);
    
    const filteredRoles = availableRoles.filter(role => {
      const currentAssignments = students.filter(student =>
        getStudentAssignedRoles(student).some(assignedRole => assignedRole.roleId === role.id)
      ).length;
      
      console.log(`Role ${role.name}: currentAssignments=${currentAssignments}, maxCount=${role.maxCount || 1}`);
      
      // 최대 할당 수에 도달하지 않은 역할만 반환
      const isAvailable = currentAssignments < (role.maxCount || 1);
      console.log(`Role ${role.name} is available:`, isAvailable);
      
      return isAvailable;
    });
    
    console.log('Filtered available roles:', filteredRoles);
    return filteredRoles;
  };

  const handleStudentRowClick = async (student: Student) => {
    setSelectedStudent(student);
    setOpenPointHistoryDialog(true);
    setLoadingHistory(true);
    try {
      const historyRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointHistories`);
      // studentId로 조회
      const q1 = query(historyRef, where('studentId', '==', student.uid), orderBy('createdAt', 'desc'));
      const snapshot1 = await getDocs(q1);
      // uid로 조회
      const q2 = query(historyRef, where('uid', '==', student.uid), orderBy('createdAt', 'desc'));
      const snapshot2 = await getDocs(q2);
      // 두 결과 합치고 중복 제거
      const allDocs = [...snapshot1.docs, ...snapshot2.docs];
      const uniqueMap = new Map();
      allDocs.forEach(doc => {
        uniqueMap.set(doc.id, doc);
      });
      const history = Array.from(uniqueMap.values()).map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate()
        };
      }) as PointHistory[];
      // 최신순 정렬
      history.sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
      setPointHistory(history);
    } catch (err) {
      setPointHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // 학생 정보 수정 다이얼로그 열기
  const handleOpenEditDialog = (student: Student) => {
    setEditingStudent(student);
    setOpenEditDialog(true);
  };

  // 학생 정보 수정 다이얼로그 닫기
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingStudent(null);
  };

  // 학생 정보 저장
  const handleSaveStudentInfo = async (
    name: string,
    number: number,
    points: number,
    characterStats: CharacterStats,
    characterLevel: number
  ) => {
    if (!editingStudent) return;

    try {
      const studentRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students/${editingStudent.uid}`);
      await updateDoc(studentRef, {
        name,
        number,
        points,
        characterStats,
        characterLevel,
        updatedAt: new Date()
      });

      // 로컬 상태 업데이트
      setStudents(prev => prev.map(s => 
        s.uid === editingStudent.uid 
          ? { ...s, name, number, points, characterStats, characterLevel, updatedAt: new Date() }
          : s
      ));

      setSuccess('학생 정보가 수정되었습니다.');
    } catch (err) {
      console.error('학생 정보 수정 중 오류:', err);
      setError('학생 정보 수정에 실패했습니다.');
    }
  };

  // 포인트 수정 다이얼로그 열기
  const handleOpenPointEditDialog = async (student: Student) => {
    setPointEditStudent(student);
    setOpenPointEditDialog(true);
    
    // 포인트 내역 불러오기
    setLoadingHistory(true);
    try {
      const historyRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointHistories`);
      const q1 = query(historyRef, where('studentId', '==', student.uid), orderBy('createdAt', 'desc'));
      const snapshot1 = await getDocs(q1);
      const q2 = query(historyRef, where('uid', '==', student.uid), orderBy('createdAt', 'desc'));
      const snapshot2 = await getDocs(q2);
      
      const allDocs = [...snapshot1.docs, ...snapshot2.docs];
      const uniqueMap = new Map();
      allDocs.forEach(doc => {
        uniqueMap.set(doc.id, doc);
      });
      
      const history = Array.from(uniqueMap.values()).map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate()
        };
      }) as PointHistory[];
      
      history.sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
      setPointHistory(history);
    } catch (err) {
      setPointHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // 포인트 수정 다이얼로그 닫기
  const handleClosePointEditDialog = () => {
    setOpenPointEditDialog(false);
    setPointEditStudent(null);
    setPointHistory([]);
  };

  // 포인트 조정 저장
  const handleSavePointAdjustment = async (adjustment: number, reason: string) => {
    if (!pointEditStudent || adjustment === 0) return;

    try {
      const newPoints = (pointEditStudent.points || 0) + adjustment;
      const studentRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students/${pointEditStudent.uid}`);
      
      await updateDoc(studentRef, {
        points: Math.max(0, newPoints),
        updatedAt: new Date()
      });

      // 포인트 히스토리 추가
      const historyRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointHistories`);
      await addDoc(historyRef, {
        studentId: pointEditStudent.uid,
        studentName: pointEditStudent.name,
        amount: Math.abs(adjustment),
        type: adjustment > 0 ? 'add' : 'subtract',
        reason,
        createdAt: new Date(),
        teacherId: teacher.uid,
        teacherName: teacher.name,
        balance: Math.max(0, newPoints)
      });

      // 학생에게 알림 생성
      try {
        await createPointNotification(
          pointEditStudent.uid,
          adjustment,
          reason,
          teacher.uid || '',
          teacher.name,
          teacher.schoolId,
          teacher.classId
        );
      } catch (notificationError) {
        console.error('알림 생성 중 오류:', notificationError);
        // 알림 생성 실패해도 포인트 수정은 성공으로 처리
      }

      // 로컬 상태 업데이트
      setStudents(prev => prev.map(s => 
        s.uid === pointEditStudent.uid 
          ? { ...s, points: Math.max(0, newPoints), updatedAt: new Date() }
          : s
      ));

      setSuccess('포인트가 수정되었습니다.');
    } catch (err) {
      console.error('포인트 수정 중 오류:', err);
      setError('포인트 수정에 실패했습니다.');
    }
  };

  // 학생 정보 보기 다이얼로그 열기
  const handleOpenInfoDialog = (student: Student) => {
    setInfoStudent(student);
    setOpenInfoDialog(true);
  };

  // 학생 정보 보기 다이얼로그 닫기
  const handleCloseInfoDialog = () => {
    setOpenInfoDialog(false);
    setInfoStudent(null);
  };

  // 학생 승인 처리
  const handleApproveStudent = async (student: Student) => {
    try {
      setLoading(true);
      
      // student_requests 컬렉션 업데이트
      const studentRequestRef = doc(db, COLLECTIONS.STUDENT_REQUESTS, student.uid);
      await updateDoc(studentRequestRef, {
        status: 'approved',
        updatedAt: new Date()
      });
      
      // 승인된 학생 정보를 students 컬렉션에 추가
      const studentRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students/${student.uid}`);
      const studentRecord = { ...(student as Student & { password?: string }) };
      delete studentRecord.password;
      await setDoc(studentRef, {
        ...studentRecord,
        status: 'active',
        isApproved: true,
        approvedAt: new Date(),
        approvedBy: teacher.uid,
        updatedAt: new Date()
      });

      const lt = (student as { loginType?: string }).loginType;
      await upsertUsersRootProfile({
        uid: student.uid,
        email: student.email || '',
        name: student.name || '',
        role: 'student',
        schoolId: teacher.schoolId,
        loginType: lt === 'password' || !lt ? 'local' : lt,
        isActive: true,
      });
      
      setSuccess('학생이 승인되었습니다.');
    } catch (err) {
      console.error('학생 승인 중 오류:', err);
      setError('학생 승인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 학생 거절 처리
  const handleRejectStudent = async (student: Student) => {
    try {
      setLoading(true);
      
      // student_requests 컬렉션 업데이트
      const studentRequestRef = doc(db, COLLECTIONS.STUDENT_REQUESTS, student.uid);
      await updateDoc(studentRequestRef, {
        status: 'rejected',
        updatedAt: new Date()
      });
      
      setSuccess('학생 가입 신청이 거절되었습니다.');
    } catch (err) {
      console.error('학생 거절 중 오류:', err);
      setError('학생 거절에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 모두 승인 처리
  const handleApproveAll = async () => {
    try {
      setLoading(true);
      
      const batch = writeBatch(db);
      
      pendingStudents.forEach(student => {
        // student_requests 컬렉션 업데이트
        const studentRequestRef = doc(db, COLLECTIONS.STUDENT_REQUESTS, student.uid);
        batch.update(studentRequestRef, {
          status: 'approved',
          updatedAt: new Date()
        });
        
        // 승인된 학생 정보를 students 컬렉션에 추가
        const studentRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students/${student.uid}`);
        batch.set(studentRef, {
          ...student,
          status: 'active',
          isApproved: true,
          approvedAt: new Date(),
          approvedBy: teacher.uid,
          updatedAt: new Date()
        });
      });
      
      await batch.commit();

      await Promise.all(
        pendingStudents.map((student) => {
          const lt = (student as { loginType?: string }).loginType;
          return upsertUsersRootProfile({
            uid: student.uid,
            email: student.email || '',
            name: student.name || '',
            role: 'student',
            schoolId: teacher.schoolId,
            loginType: lt === 'password' || !lt ? 'local' : lt,
            isActive: true,
          });
        })
      );
      
      setSuccess(`${pendingStudents.length}명의 학생이 모두 승인되었습니다.`);
    } catch (err) {
      console.error('모두 승인 중 오류:', err);
      setError('모두 승인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 에러/성공 메시지 자동 숨김
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <div style={{ 
      width: '100%',
      minHeight: '100vh',
      padding: isMobile ? '16px' : '0',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* 성공 메시지 */}
      {success && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 24px',
          background: '#4caf50',
          color: 'white',
          borderRadius: '8px',
          zIndex: 10000,
          fontSize: '14px',
          fontFamily: 'Pretendard',
          fontWeight: '500',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          cursor: 'pointer'
        }}
        onClick={() => setSuccess(null)}
        >
          {success}
        </div>
      )}
      {/* 에러 메시지 */}
      {error && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 24px',
          background: '#f44336',
          color: 'white',
          borderRadius: '8px',
          zIndex: 10000,
          fontSize: '14px',
          fontFamily: 'Pretendard',
          fontWeight: '500',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          cursor: 'pointer'
        }}
        onClick={() => setError(null)}
        >
          {error}
        </div>
      )}
      {/* 헤더 영역 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '4px' : '8px',
        marginBottom: isMobile ? '20px' : '32px'
      }}>
        {/* 학년반 정보 */}
        <div style={{
          color: 'var(--Color-text-normal, #161616)',
          fontSize: isMobile ? '14px' : '16px',
          fontFamily: 'Pretendard',
          fontWeight: '400',
          lineHeight: isMobile ? '20px' : '24px'
        }}>
          {teacher.homeroomClass?.grade}학년 {teacher.homeroomClass?.class}반
        </div>
        
        {/* 제목과 버튼 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: '20px',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <h1 style={{
            color: 'var(--Color-text-normal, #161616)',
            fontSize: isMobile ? '24px' : '32px',
            fontFamily: 'Pretendard',
            fontWeight: '600',
            lineHeight: isMobile ? '36px' : '48px',
            margin: 0
          }}>
            학급 관리
          </h1>
          {teacher.homeroomClass && (
            <Button
              width={isMobile ? "100%" : "140px"}
              height="40px"
              variant="submit"
              onClick={generateClassCode}
              disabled={loading || !teacher.homeroomClass.grade || !teacher.homeroomClass.class}
              leftIcon={<img src="/images/icons/qrCode.svg" alt="QR" style={{ width: '16px', height: '16px' }} />}
            >
              학급 코드 생성
            </Button>
          )}
        </div>
      </div>

      {/* 메인 컨텐츠 영역 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        flex: 1,
        overflow: 'hidden',
        minHeight: 0
      }}>
          {!teacher.homeroomClass ? (
          <div style={{
            padding: '16px',
            background: '#FFF3E0',
            borderRadius: '8px',
            color: '#E65100'
          }}>
              담임 학급이 설정되어 있지 않습니다. 담임 학급을 추가해주세요.
          </div>
          ) : (
            <>
            {/* 탭 버튼 */}
            <div style={{
              display: 'flex',
              gap: isMobile ? '8px' : '16px',
              alignItems: 'center'
            }}>
              <div 
                style={{
                  height: isMobile ? '36px' : '40px',
                  padding: isMobile ? '0 16px' : '0 12px',
                  background: tabValue === 0 ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flex: isMobile ? 1 : 'none'
                }}
                onClick={() => {
                  setTabValue(0);
                  setCurrentPage(1); // 탭 전환 시 1페이지로
                }}
              >
                <span style={{
                  color: tabValue === 0 ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                  fontSize: isMobile ? '13px' : '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: isMobile ? '19.5px' : '21px'
                }}>
                  학생 리스트
                </span>
              </div>
              
              <div 
                style={{
                  height: isMobile ? '36px' : '40px',
                  padding: isMobile ? '0 16px' : '0 12px',
                  background: tabValue === 1 ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flex: isMobile ? 1 : 'none'
                }}
                onClick={() => {
                  setTabValue(1);
                  setCurrentPage(1); // 탭 전환 시 1페이지로
                }}
              >
                <span style={{
                  color: tabValue === 1 ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                  fontSize: isMobile ? '13px' : '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: isMobile ? '19.5px' : '21px'
                }}>
                  승인 대기 목록
                </span>
              </div>
            </div>


            {tabValue === 0 && (
              <>
                {/* 검색 및 필터 영역 */}
                <div style={{
                  display: 'flex',
                  gap: isMobile ? '8px' : '12px',
                  alignItems: 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between'
                }}>
                  <div style={{
                    display: 'flex',
                    gap: isMobile ? '8px' : '12px',
                    alignItems: 'center',
                    flex: 1
                  }}>
                  {/* 검색창 */}
                  <div style={{
                    flex: 1,
                    width: isMobile ? '100%' : 'auto',
                    height: isMobile ? '36px' : '40px',
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    background: 'var(--Color-fill-white, white)',
                    borderRadius: '999px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <SearchIcon width={isMobile ? 18 : 20} height={isMobile ? 18 : 20} color="#989898" />
                    <input
                      type="text"
                      placeholder="검색어를 입력해 주세요.(이름 또는 이메일)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: isMobile ? '12px' : '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '400',
                        lineHeight: isMobile ? '18px' : '19.5px'
                      }}
                    />
                  </div>
                  
                  {/* 페이지당 개수 선택 - 모바일에서는 숨김 */}
                  {!isMobile && (
                    <div style={{
                      width: '112px',
                      height: '40px',
                      padding: '12px',
                      background: 'var(--Color-fill-white, white)',
                      borderRadius: '8px',
                      border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer'
                    }}>
                      <span style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.5px',
                        whiteSpace: 'nowrap'
                      }}>
                        10개씩 보기
                      </span>
                      <img src="/images/icons/arrowDown.svg" alt="펼치기" style={{ width: '24px', height: '24px' }} />
                    </div>
                  )}
                  </div>
                  
                  {/* 일괄 삭제 버튼 */}
                  {selectedStudents.size > 0 && (
                    <Button
                      variant="cancel"
                      onClick={handleBatchDelete}
                      width={isMobile ? "100%" : "auto"}
                      height="40px"
                      style={{
                        backgroundColor: '#E57373',
                        color: 'white',
                        border: 'none',
                        padding: '0 16px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      선택한 {selectedStudents.size}명 삭제
                    </Button>
                  )}
                </div>

                {/* 학생 테이블/카드 */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: isMobile ? 1 : 'none',
                  minHeight: 0,
                  overflow: 'hidden'
                }}>
                  <Card
                    width="100%"
                    height={isMobile ? "100%" : "514px"}
                    padding={isMobile ? "16px" : "16px 20px"}
                  >
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      minHeight: 0
                    }}>
                    {/* 테이블 헤더 */}
                    <div style={{
                      display: 'flex',
                      borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                      paddingBottom: '12px',
                      flexShrink: 0
                    }}>
                      {/* 체크박스 컬럼 */}
                      <div style={{ flex: '0 0 40px', paddingLeft: isMobile ? '0' : '16px', paddingRight: '8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <input
                          type="checkbox"
                          checked={selectedStudents.size === filteredStudents.length && filteredStudents.length > 0}
                          onChange={handleSelectAll}
                          style={{
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer'
                          }}
                        />
                      </div>
                      <div style={{ flex: isMobile ? '0 0 50px' : '0 0 103px', paddingLeft: '8px', paddingRight: isMobile ? '8px' : '16px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <span style={{
                          color: 'var(--Color-text-assistive, #989898)',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '19.5px'
                        }}>No</span>
                      </div>
                      <div style={{ flex: isMobile ? '1' : '0 0 151px', paddingLeft: '8px', paddingRight: '8px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <span style={{
                          color: 'var(--Color-text-assistive, #989898)',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '19.5px'
                        }}>이름</span>
                        {!isMobile && (
                          <img 
                            src="/images/icons/sort.svg" 
                            alt="정렬" 
                            style={{ 
                              width: '20px', 
                              height: '20px', 
                              cursor: 'pointer',
                              opacity: sortBy === 'name' ? 1 : 0.5,
                              transform: sortBy === 'name' && sortOrder === 'desc' ? 'rotate(180deg)' : 'none',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => handleSort('name')}
                          />
                        )}
                      </div>
                      {!isMobile && (
                        <>
                          <div style={{ width: '102px', paddingLeft: '8px', paddingRight: '8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <span style={{
                              color: 'var(--Color-text-assistive, #989898)',
                              fontSize: '13px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '19.5px'
                            }}>학번</span>
                          </div>
                          <div style={{ width: '236px', paddingLeft: '8px', paddingRight: '8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <span style={{
                              color: 'var(--Color-text-assistive, #989898)',
                              fontSize: '13px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '19.5px'
                            }}>이메일</span>
                          </div>
                          <div style={{ width: '213px', paddingLeft: '8px', paddingRight: '8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <span style={{
                              color: 'var(--Color-text-assistive, #989898)',
                              fontSize: '13px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '19.5px'
                            }}>전화</span>
                          </div>
                        </>
                      )}
                      <div style={{ flex: isMobile ? '0 0 auto' : '0 0 166px', paddingLeft: '8px', paddingRight: '8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '2px' }}>
                        <span style={{
                          color: 'var(--Color-text-assistive, #989898)',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '19.5px'
                        }}>포인트</span>
                        {!isMobile && (
                          <img 
                            src="/images/icons/sort.svg" 
                            alt="정렬" 
                            style={{ 
                              width: '20px', 
                              height: '20px', 
                              cursor: 'pointer',
                              opacity: sortBy === 'points' ? 1 : 0.5,
                              transform: sortBy === 'points' && sortOrder === 'desc' ? 'rotate(180deg)' : 'none',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => handleSort('points')}
                          />
                        )}
                      </div>
                      {!isMobile && (
                        <>
                          <div style={{ width: '204px', paddingLeft: '8px', paddingRight: '8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <span style={{
                              color: 'var(--Color-text-assistive, #989898)',
                              fontSize: '13px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '19.5px'
                            }}>역할</span>
                          </div>
                          <div style={{ width: '65px', paddingLeft: '8px', paddingRight: '8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <span style={{
                              color: 'var(--Color-text-assistive, #989898)',
                              fontSize: '13px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '19.5px'
                            }}>관리</span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* 테이블 바디 */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      overflowY: 'auto',
                      flex: isMobile ? 1 : 'none',
                      minHeight: 0,
                      maxHeight: isMobile ? 'none' : '430px'
                    }}>
                      {loading ? (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          padding: '24px',
                          color: 'var(--Color-text-assistive, #989898)'
                        }}>
                          로딩 중...
                        </div>
                      ) : (isMobile ? filteredStudents : currentStudents) && (isMobile ? filteredStudents : currentStudents).length > 0 ? (
                        (isMobile ? filteredStudents : currentStudents).map((student, index) => (
                          <div
                            key={student.uid}
                            style={{
                              display: 'flex',
                              borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                              alignItems: 'center',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={!isMobile ? (e) => {
                              e.currentTarget.style.background = 'var(--Color-background-sub, #F8F8F8)';
                            } : undefined}
                            onMouseLeave={!isMobile ? (e) => {
                              e.currentTarget.style.background = 'transparent';
                            } : undefined}
                          >
                            {/* 체크박스: div onClick + input onChange 중복 시 토글이 두 번 일어나 한 명만 선택되는 현상 방지 */}
                            <label
                              htmlFor={`class-mgmt-select-${student.uid}`}
                              style={{
                                flex: '0 0 40px',
                                padding: '12px 8px',
                                paddingLeft: isMobile ? '0' : '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                cursor: 'pointer',
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                id={`class-mgmt-select-${student.uid}`}
                                type="checkbox"
                                checked={selectedStudents.has(student.uid)}
                                onChange={() => handleSelectStudent(student.uid)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  cursor: 'pointer',
                                }}
                              />
                            </label>
                            <div style={{ flex: isMobile ? '0 0 50px' : '0 0 103px', paddingTop: '12px', paddingBottom: '12px', paddingLeft: '8px', paddingRight: isMobile ? '8px' : '16px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                              <span style={{
                                color: 'var(--Color-text-normal, #161616)',
                                fontSize: '14px',
                                fontFamily: 'Pretendard',
                                fontWeight: '400',
                                lineHeight: '21px'
                              }}>{isMobile ? index + 1 : startIndex + index + 1}</span>
                            </div>
                            <div 
                              style={{ flex: isMobile ? '1' : '0 0 151px', padding: '12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenInfoDialog(student);
                              }}
                            >
                              <span style={{
                                color: 'var(--Color-text-brand-color, #FF6F0F)',
                                fontSize: '14px',
                                fontFamily: 'Pretendard',
                                fontWeight: '400',
                                lineHeight: '21px',
                                cursor: 'pointer',
                                textDecoration: 'underline'
                              }}>{student.name}</span>
                            </div>
                            {!isMobile && (
                              <>
                                <div style={{ width: '102px', padding: '12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                  <span style={{
                                    color: 'var(--Color-text-normal, #161616)',
                                    fontSize: '14px',
                                    fontFamily: 'Pretendard',
                                    fontWeight: '400',
                                    lineHeight: '21px'
                                  }}>{student.number}번</span>
                                </div>
                                <div style={{ width: '235px', padding: '12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                  <span style={{
                                    color: 'var(--Color-text-normal, #161616)',
                                    fontSize: '14px',
                                    fontFamily: 'Pretendard',
                                    fontWeight: '400',
                                    lineHeight: '21px'
                                  }}>{student.email}</span>
                                </div>
                                <div style={{ width: '213px', padding: '12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                  <span style={{
                                    color: 'var(--Color-text-normal, #161616)',
                                    fontSize: '14px',
                                    fontFamily: 'Pretendard',
                                    fontWeight: '400',
                                    lineHeight: '21px'
                                  }}>010-1234-1234</span>
                                </div>
                              </>
                            )}
                            <div style={{ flex: isMobile ? '0 0 auto' : '0 0 166px', padding: '12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '8px' }}>
                              <span style={{
                                color: 'var(--Color-text-normal, #161616)',
                                fontSize: '14px',
                                fontFamily: 'Pretendard',
                                fontWeight: '400',
                                lineHeight: '21px'
                              }}>{student.points || 0} 점</span>
                              <div 
                                style={{ 
                                  width: '20px', 
                                  height: '20px', 
                                  cursor: 'pointer', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center' 
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenPointEditDialog(student);
                                }}
                              >
                                <EditIcon width={12.8} height={12.8} color="#989898" />
                              </div>
                            </div>
                            {!isMobile && (
                              <>
                                <div style={{ width: '205px', height: '45px', padding: '0 8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                  {getStudentAssignedRoles(student).map((assignedRole, roleIndex) => {
                                    const roleInfo = findClassRole(assignedRole.roleId);
                                    if (!roleInfo) return null;
                                    return (
                                      <div
                                        key={`${student.uid}-${roleInfo.id}-${roleIndex}`}
                                        style={{
                                          padding: '4px 8px',
                                          background: 'var(--Color-fill-normal, #293044)',
                                          borderRadius: '4px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '2px'
                                        }}
                                      >
                                        <span style={{
                                          color: 'var(--Color-text-inverse, white)',
                                          fontSize: '11px',
                                          fontFamily: 'Pretendard',
                                          fontWeight: '400',
                                          lineHeight: '16.5px'
                                        }}>{roleInfo.name}</span>
                                        <div 
                                          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveRole(student.uid, roleInfo.id);
                                          }}
                                        >
                                          <CloseCircleIcon width={12} height={13} color="white" />
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {getStudentAssignedRoles(student).filter(ar => findClassRole(ar.roleId)).length < MAX_ROLES_PER_STUDENT && (
                                    <div 
                                      style={{ width: '20px', height: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenRoleDialog(student);
                                      }}
                                    >
                                      <PlusCircleIcon width={12.8} height={12.8} color="#161616" />
                                    </div>
                                  )}
                                </div>
                                <div style={{ height: '45px', padding: '12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '9px' }}>
                                  <div 
                                    style={{ width: '20px', height: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenEditDialog(student);
                                    }}
                                  >
                                    <SettingIcon width={14.1} height={13.6} color="#989898" />
                                  </div>
                                  <div 
                                    style={{ width: '20px', height: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(student);
                                    }}
                                  >
                                    <TrashIcon width={11.47} height={12.8} color="#989898" />
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        ))
                      ) : (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          padding: '24px',
                          color: 'var(--Color-text-assistive, #989898)',
                          fontSize: '14px',
                          fontFamily: 'Pretendard'
                        }}>
                          등록된 학생이 없습니다.
                        </div>
                      )}
                    </div>
                  </div>
                  </Card>
                </div>

                {/* 페이지네이션 (데스크톱만) */}
                {!isMobile && filteredStudents.length > 0 && (
                  <div style={{
                    padding: '20px 0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {/* 맨 처음으로 */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div 
                        style={{ 
                          cursor: currentPage === 1 ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px'
                        }}
                        onClick={currentPage === 1 ? undefined : goToFirstPage}
                      >
                        <LeftArrowDoubleIcon 
                          width={24} 
                          height={24} 
                          color={currentPage === 1 ? '#989898' : '#161616'}
                        />
                      </div>
                      {/* 이전 페이지 */}
                      <div 
                        style={{ 
                          cursor: currentPage === 1 ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px'
                        }}
                        onClick={currentPage === 1 ? undefined : goToPreviousPage}
                      >
                        <LeftArrowIcon 
                          width={24} 
                          height={24} 
                          color={currentPage === 1 ? '#989898' : '#161616'}
                        />
                      </div>
                    </div>

                    {/* 페이지 번호 - 모든 페이지 표시 */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {[...Array(totalPages)].map((_, i) => {
                        const pageNum = i + 1;
                        
                        return (
                          <div
                            key={pageNum}
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={() => goToPage(pageNum)}
                          >
                            <span style={{
                              textAlign: 'center',
                              color: pageNum === currentPage 
                                ? 'var(--Color-text-normal, #161616)' 
                                : 'var(--Color-text-assistive, #989898)',
                              fontSize: '14px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '14px'
                            }}>
                              {pageNum}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* 다음으로 */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {/* 다음 페이지 */}
                      <div 
                        style={{ 
                          cursor: currentPage === totalPages ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px'
                        }}
                        onClick={currentPage === totalPages ? undefined : goToNextPage}
                      >
                        <RightArrowIcon 
                          width={24} 
                          height={24} 
                          color={currentPage === totalPages ? '#989898' : '#161616'}
                        />
                      </div>
                      {/* 맨 끝으로 */}
                      <div 
                        style={{ 
                          cursor: currentPage === totalPages ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px'
                        }}
                        onClick={currentPage === totalPages ? undefined : goToLastPage}
                      >
                        <RightArrowDoubleIcon 
                          width={24} 
                          height={24} 
                          color={currentPage === totalPages ? '#989898' : '#161616'}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* 승인 대기 목록 탭 */}
            {tabValue === 1 && (
              <>
                {/* 상단 정보 및 버튼 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '12px' : '0'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      color: 'var(--Color-text-brand-color, #FF6F0F)',
                      fontSize: isMobile ? '16px' : '18px',
                      fontFamily: 'Pretendard',
                      fontWeight: '600',
                      lineHeight: isMobile ? '24px' : '27px'
                    }}>
                      {pendingStudents.length}
                    </span>
                    <span style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: isMobile ? '16px' : '18px',
                      fontFamily: 'Pretendard',
                      fontWeight: '600',
                      lineHeight: isMobile ? '24px' : '27px'
                    }}>
                      명의 학생이 승인 대기 중 입니다
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '12px', width: isMobile ? '100%' : 'auto' }}>
                    <Button
                      width={isMobile ? "100%" : "118px"}
                      height="40px"
                      variant="submit"
                      onClick={handleApproveAll}
                      disabled={pendingStudents.length === 0}
                    >
                      모두 승인
            </Button>
                    
                    {!isMobile && (
                      <div style={{
                        width: '112px',
                        height: '40px',
                        padding: '12px',
                        background: 'var(--Color-fill-white, white)',
                        borderRadius: '8px',
                        border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer'
                      }}>
                        <span style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '19.5px',
                          whiteSpace: 'nowrap'
                        }}>
                          10개씩 보기
                        </span>
                        <img src="/images/icons/arrowDown.svg" alt="펼치기" style={{ width: '24px', height: '24px' }} />
                      </div>
                    )}
                  </div>
                </div>

                {/* 승인 대기 테이블/카드 */}
                {isMobile ? (
                  // 모바일 카드 형식 (스크롤)
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    maxHeight: 'calc(100vh - 300px)',
                    overflowY: 'auto',
                    paddingRight: '4px'
                  }}>
                    {pendingStudents.length > 0 ? (
                      pendingStudents.map((student, index) => (
                        <Card
                          key={student.uid}
                          width="100%"
                          padding="16px"
                        >
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                          }}>
                            {/* 상단: 번호, 이름 */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <span style={{
                                  color: 'var(--Color-text-assistive, #989898)',
                                  fontSize: '12px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '18px'
                                }}>#{index + 1}</span>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '16px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '600',
                                  lineHeight: '24px'
                                }}>
                                  {student.name}
                                </span>
                              </div>
                            </div>
                            
                            {/* 중간: 학급, 학번, 이메일 */}
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <span style={{
                                  color: 'var(--Color-text-assistive, #989898)',
                                  fontSize: '12px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '18px'
                                }}>학급:</span>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '13px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '19.5px'
                                }}>{student.grade}학년 {student.class}반</span>
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <span style={{
                                  color: 'var(--Color-text-assistive, #989898)',
                                  fontSize: '12px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '18px'
                                }}>학번:</span>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '13px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '19.5px'
                                }}>{student.number}번</span>
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <span style={{
                                  color: 'var(--Color-text-assistive, #989898)',
                                  fontSize: '12px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '18px'
                                }}>이메일:</span>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '13px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '19.5px',
                                  wordBreak: 'break-all'
                                }}>{student.email}</span>
                              </div>
                            </div>
                            
                            {/* 하단: 승인, 거절 버튼 */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              paddingTop: '8px',
                              borderTop: '1px solid var(--Color-line-normal, #F0F0F0)',
                              gap: '8px'
                            }}>
                              <Button
                                width="100%"
                                height="36px"
                                variant="submit"
                                onClick={() => handleApproveStudent(student)}
                              >
                                승인
                              </Button>
                              <div 
                                style={{ 
                                  width: '36px',
                                  height: '36px',
                                  cursor: 'pointer', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  border: '1px solid var(--Color-line-normal, #F0F0F0)',
                                  borderRadius: '8px'
                                }}
                                onClick={() => handleRejectStudent(student)}
                              >
                                <TrashIcon width={18} height={18} color="#989898" />
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))
                    ) : (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '24px',
                        color: 'var(--Color-text-assistive, #989898)',
                        fontSize: '14px',
                        fontFamily: 'Pretendard'
                      }}>
                        승인 대기 중인 학생이 없습니다.
                      </div>
                    )}
                  </div>
                ) : (
                  // 데스크톱 테이블 형식
                  <Card
                    width="100%"
                    height="514px"
                    padding="16px 20px"
                  >
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      {/* 테이블 헤더 */}
                      <div style={{
                        display: 'flex',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        paddingBottom: '12px'
                      }}>
                        <div style={{ width: '103px', paddingLeft: '16px', paddingRight: '16px' }}>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '19.5px'
                          }}>No</span>
                        </div>
                        <div style={{ width: '102px', paddingLeft: '8px', paddingRight: '8px' }}>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '19.5px'
                          }}>학급</span>
                        </div>
                        <div style={{ width: '102px', paddingLeft: '8px', paddingRight: '8px' }}>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '19.5px'
                          }}>학번</span>
                        </div>
                        <div style={{ width: '151px', paddingLeft: '8px', paddingRight: '8px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '19.5px'
                          }}>이름</span>
                          <img 
                            src="/images/icons/sort.svg" 
                            alt="정렬" 
                            style={{ 
                              width: '20px', 
                              height: '20px', 
                              cursor: 'pointer',
                              opacity: sortBy === 'name' ? 1 : 0.5,
                              transform: sortBy === 'name' && sortOrder === 'desc' ? 'rotate(180deg)' : 'none',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => handleSort('name')}
                          />
                        </div>
                        <div style={{ width: '236px', paddingLeft: '8px', paddingRight: '8px' }}>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '19.5px'
                          }}>이메일</span>
                        </div>
                        <div style={{ width: '213px', paddingLeft: '8px', paddingRight: '8px' }}>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '19.5px'
                          }}>전화</span>
                        </div>
                        <div style={{ width: '166px', paddingLeft: '8px', paddingRight: '8px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '19.5px'
                          }}>신청일시</span>
                          <img 
                            src="/images/icons/sort.svg" 
                            alt="정렬" 
                            style={{ 
                              width: '20px', 
                              height: '20px', 
                              cursor: 'pointer',
                              opacity: 0.5
                            }}
                          />
                        </div>
                        <div style={{ width: '90px', paddingLeft: '8px', paddingRight: '8px' }}>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '19.5px'
                          }}>승인</span>
                        </div>
                        <div style={{ width: '65px', paddingLeft: '8px', paddingRight: '8px' }}>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '19.5px'
                          }}>관리</span>
                        </div>
                      </div>

                      {/* 테이블 바디 */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'auto',
                        maxHeight: '430px'
                      }}>
                        {pendingStudents.length > 0 ? (
                          pendingStudents.slice(startIndex, endIndex).map((student, index) => (
                            <div
                              key={student.uid}
                              style={{
                                display: 'flex',
                                borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                                alignItems: 'center'
                              }}
                            >
                              <div style={{ width: '103px', padding: '12px 18px' }}>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '14px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '21px'
                                }}>{startIndex + index + 1}</span>
                              </div>
                              <div style={{ width: '102px', padding: '12px 8px' }}>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '14px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '21px'
                                }}>{student.grade}학년 {student.class}반</span>
                              </div>
                              <div style={{ width: '102px', padding: '12px 8px' }}>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '14px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '21px'
                                }}>{student.number}번</span>
                              </div>
                              <div style={{ width: '151px', padding: '12px 8px' }}>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '14px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '21px'
                                }}>{student.name}</span>
                              </div>
                              <div style={{ width: '235px', padding: '12px 8px' }}>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '14px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '21px'
                                }}>{student.email}</span>
                              </div>
                              <div style={{ width: '213px', padding: '12px 8px' }}>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '14px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '21px'
                                }}>010-1234-1234</span>
                              </div>
                              <div style={{ width: '166px', padding: '12px 8px' }}>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '14px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '21px'
                                }}>2025.05.08 00:00</span>
                              </div>
                              <div style={{ width: '90px', height: '45px', padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                                <div
                                  style={{
                                    width: '66px',
                                    padding: '4px 8px',
                                    background: 'var(--Color-fill-normal, #293044)',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => handleApproveStudent(student)}
                                >
                                  <span style={{
                                    color: 'var(--Color-text-inverse, white)',
                                    fontSize: '11px',
                                    fontFamily: 'Pretendard',
                                    fontWeight: '400',
                                    lineHeight: '16.5px'
                                  }}>승인</span>
                                </div>
                              </div>
                              <div style={{ height: '45px', padding: '12px 8px', display: 'flex', alignItems: 'center' }}>
                                <div 
                                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  onClick={() => handleRejectStudent(student)}
                                >
                                  <TrashIcon width={20} height={20} color="#989898" />
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '24px',
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '14px',
                            fontFamily: 'Pretendard'
                          }}>
                            승인 대기 중인 학생이 없습니다.
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                )}

                {/* 페이지네이션 (데스크톱만) */}
                {!isMobile && pendingStudents.length > 0 && (
                  <div style={{
                    padding: '20px 0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {/* 맨 처음으로 */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div 
                        style={{ 
                          cursor: currentPage === 1 ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px'
                        }}
                        onClick={currentPage === 1 ? undefined : goToFirstPage}
                      >
                        <LeftArrowDoubleIcon 
                          width={24} 
                          height={24} 
                          color={currentPage === 1 ? '#989898' : '#161616'}
                        />
                      </div>
                      <div 
                        style={{ 
                          cursor: currentPage === 1 ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px'
                        }}
                        onClick={currentPage === 1 ? undefined : goToPreviousPage}
                      >
                        <LeftArrowIcon 
                          width={24} 
                          height={24} 
                          color={currentPage === 1 ? '#989898' : '#161616'}
                        />
                      </div>
                    </div>

                    {/* 페이지 번호 - 모든 페이지 표시 */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {[...Array(totalPages)].map((_, i) => {
                        const pageNum = i + 1;
                        
                        return (
                          <div
                            key={pageNum}
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={() => goToPage(pageNum)}
                          >
                            <span style={{
                              textAlign: 'center',
                              color: pageNum === currentPage 
                                ? 'var(--Color-text-normal, #161616)' 
                                : 'var(--Color-text-assistive, #989898)',
                              fontSize: '14px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '14px'
                            }}>
                              {pageNum}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* 다음으로 */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div 
                        style={{ 
                          cursor: currentPage === totalPages ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px'
                        }}
                        onClick={currentPage === totalPages ? undefined : goToNextPage}
                      >
                        <RightArrowIcon 
                          width={24} 
                          height={24} 
                          color={currentPage === totalPages ? '#989898' : '#161616'}
                        />
                      </div>
                      <div 
                        style={{ 
                          cursor: currentPage === totalPages ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px'
                        }}
                        onClick={currentPage === totalPages ? undefined : goToLastPage}
                      >
                        <RightArrowDoubleIcon 
                          width={24} 
                          height={24} 
                          color={currentPage === totalPages ? '#989898' : '#161616'}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* 학생 정보 수정 다이얼로그 */}
      {editingStudent && (
        <EditStudentInfoDialog
          isOpen={openEditDialog}
          onClose={handleCloseEditDialog}
          studentName={editingStudent.name}
          studentNumber={editingStudent.number || 0}
          studentPoints={editingStudent.points || 0}
          characterStats={editingStudent.characterStats}
          characterLevel={editingStudent.characterLevel}
          onSave={handleSaveStudentInfo}
          scale={1}
        />
      )}

      {/* 포인트 수정 다이얼로그 */}
      {pointEditStudent && (
        <StudentPointEditDialog
          isOpen={openPointEditDialog}
          onClose={handleClosePointEditDialog}
          studentName={pointEditStudent.name}
          currentPoints={pointEditStudent.points || 0}
          pointHistory={pointHistory}
          onSave={handleSavePointAdjustment}
          scale={1}
        />
      )}

      {/* 역할 부여 다이얼로그 */}
      {selectedStudent && (
        <StudentRoleAssignmentDialog
          isOpen={openRoleDialog}
          onClose={handleCloseRoleDialog}
          studentName={selectedStudent.name}
          currentRoles={getStudentAssignedRoles(selectedStudent).filter(ar => findClassRole(ar.roleId))}
          availableRoles={availableRoles.map(role => ({
            ...role,
            currentCount: students.filter(s => 
              getStudentAssignedRoles(s).some(ar => ar.roleId === role.id)
            ).length
          }))}
          maxRolesPerStudent={MAX_ROLES_PER_STUDENT}
          onAssignRole={handleAssignRole}
          onRemoveRole={(roleId) => handleRemoveRole(selectedStudent.uid, roleId)}
          scale={1}
        />
      )}

      {/* 학생 정보 보기 다이얼로그 */}
      {infoStudent && (
        <StudentInfoDialog
          isOpen={openInfoDialog}
          onClose={handleCloseInfoDialog}
          studentName={infoStudent.name}
          studentNumber={infoStudent.number || 0}
          studentEmail={infoStudent.email}
          studentPhone="010-1234-1234"
          studentPoints={infoStudent.points || 0}
          studentRoles={
            infoStudent.assignedRoles?.map(ar => {
              const roleInfo = availableRoles.find(r => r.id === ar.roleId);
              return roleInfo?.name || '';
            }).filter(name => name !== '') || []
          }
          scale={1}
        />
      )}

      {/* 학급 코드 다이얼로그 */}
      <ClassCodeDialog
        isOpen={openClassCodeDialog}
        onClose={handleCloseClassCodeDialog}
        classCode={classCode || ''}
        grade={teacher.homeroomClass?.grade}
        class={teacher.homeroomClass?.class}
        className={teacher.homeroomClass?.class ? `${teacher.homeroomClass.class}반` : undefined}
        schoolId={teacher.schoolId}
        classId={teacher.classId}
        scale={1}
      />

      {/* 학생 삭제 확인 다이얼로그 */}
      {openDeleteDialog && studentToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Pretendard',
          zIndex: 1000
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCancelDelete();
          }
        }}
        >
          <div style={{
            background: 'var(--Color-background-normal, white)',
            borderRadius: '16px',
            width: '450px',
            boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column',
            padding: '24px 28px',
            gap: '24px'
          }}>
            {/* 헤더 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                lineHeight: '30px',
                color: 'var(--Color-text-normal, #161616)'
              }}>
                학생 처리 확인
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: '21px',
                color: 'var(--Color-text-normal, #161616)'
              }}>
                {studentToDelete.name} 학생을 어떻게 처리하시겠습니까?
              </p>
            </div>

            {/* 옵션 선택 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              padding: '16px',
              background: 'var(--Color-background-sub, #F8F8F8)',
              borderRadius: '8px'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                cursor: 'pointer',
                padding: '12px',
                borderRadius: '8px',
                background: deleteType === 'remove' ? 'white' : 'transparent',
                border: deleteType === 'remove' ? '2px solid var(--Color-fill-brand-color, #FF6F0F)' : '2px solid transparent',
                transition: 'all 0.2s'
              }}
              onClick={() => setDeleteType('remove')}
              >
                <input
                  type="radio"
                  name="deleteType"
                  value="remove"
                  checked={deleteType === 'remove'}
                  onChange={() => setDeleteType('remove')}
                  style={{
                    marginTop: '2px',
                    cursor: 'pointer'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    lineHeight: '21px',
                    color: 'var(--Color-text-normal, #161616)',
                    marginBottom: '4px'
                  }}>
                    학급에서만 제거
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '400',
                    lineHeight: '18px',
                    color: 'var(--Color-text-assistive, #989898)'
                  }}>
                    이 학급의 데이터만 삭제됩니다. 학생의 계정은 유지되며 다른 학급에 있을 수 있습니다.
                  </div>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                cursor: 'pointer',
                padding: '12px',
                borderRadius: '8px',
                background: deleteType === 'delete' ? 'white' : 'transparent',
                border: deleteType === 'delete' ? '2px solid var(--Color-fill-brand-color, #FF6F0F)' : '2px solid transparent',
                transition: 'all 0.2s'
              }}
              onClick={() => setDeleteType('delete')}
              >
                <input
                  type="radio"
                  name="deleteType"
                  value="delete"
                  checked={deleteType === 'delete'}
                  onChange={() => setDeleteType('delete')}
                  style={{
                    marginTop: '2px',
                    cursor: 'pointer'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    lineHeight: '21px',
                    color: 'var(--Color-text-normal, #161616)',
                    marginBottom: '4px'
                  }}>
                    완전 삭제 (계정 삭제)
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '400',
                    lineHeight: '18px',
                    color: 'var(--Color-text-assistive, #989898)'
                  }}>
                    학생의 모든 데이터와 계정이 영구적으로 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
                  </div>
                </div>
              </label>
            </div>

            {/* 경고 메시지 */}
            {deleteType === 'delete' && (
              <div style={{
                padding: '12px 16px',
                background: '#FFEBEE',
                borderRadius: '8px',
                border: '1px solid #EF5350'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '12px',
                  fontWeight: '400',
                  lineHeight: '18px',
                  color: '#C62828'
                }}>
                  ⚠️ 이 작업은 되돌릴 수 없습니다. 학생의 모든 데이터와 계정이 영구적으로 삭제됩니다.
                </p>
              </div>
            )}

            {/* 버튼 영역 */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}>
              <Button
                variant="cancel"
                onClick={handleCancelDelete}
                width="100px"
                height="40px"
                disabled={loading}
              >
                취소
              </Button>
              <button
                onClick={handleConfirmDelete}
                disabled={loading}
                style={{
                  width: '140px',
                  height: '40px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: loading ? '#F5F5F5' : '#E57373',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  lineHeight: '21px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  fontFamily: 'Pretendard',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = '#D32F2F';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = '#E57373';
                  }
                }}
              >
                {loading ? (deleteType === 'delete' ? '계정 삭제 중...' : '제거 중...') : deleteType === 'delete' ? '완전 삭제' : '제거하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 일괄 삭제 확인 다이얼로그 */}
      {openBatchDeleteDialog && selectedStudents.size > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Pretendard',
          zIndex: 1000
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCancelBatchDelete();
          }
        }}
        >
          <div style={{
            background: 'var(--Color-background-normal, white)',
            borderRadius: '16px',
            width: '450px',
            boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column',
            padding: '24px 28px',
            gap: '24px'
          }}>
            {/* 헤더 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                lineHeight: '30px',
                color: 'var(--Color-text-normal, #161616)'
              }}>
                학생 일괄 처리 확인
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: '21px',
                color: 'var(--Color-text-normal, #161616)'
              }}>
                선택한 {selectedStudents.size}명의 학생을 어떻게 처리하시겠습니까?
              </p>
            </div>

            {/* 옵션 선택 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              padding: '16px',
              background: 'var(--Color-background-sub, #F8F8F8)',
              borderRadius: '8px'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                cursor: 'pointer',
                padding: '12px',
                borderRadius: '8px',
                background: batchDeleteType === 'remove' ? 'white' : 'transparent',
                border: batchDeleteType === 'remove' ? '2px solid var(--Color-fill-brand-color, #FF6F0F)' : '2px solid transparent',
                transition: 'all 0.2s'
              }}
              onClick={() => setBatchDeleteType('remove')}
              >
                <input
                  type="radio"
                  name="batchDeleteType"
                  value="remove"
                  checked={batchDeleteType === 'remove'}
                  onChange={() => setBatchDeleteType('remove')}
                  style={{
                    marginTop: '2px',
                    cursor: 'pointer'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    lineHeight: '21px',
                    color: 'var(--Color-text-normal, #161616)',
                    marginBottom: '4px'
                  }}>
                    학급에서만 제거
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '400',
                    lineHeight: '18px',
                    color: 'var(--Color-text-assistive, #989898)'
                  }}>
                    이 학급의 데이터만 삭제됩니다. 학생의 계정은 유지되며 다른 학급에 있을 수 있습니다.
                  </div>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                cursor: 'pointer',
                padding: '12px',
                borderRadius: '8px',
                background: batchDeleteType === 'delete' ? 'white' : 'transparent',
                border: batchDeleteType === 'delete' ? '2px solid var(--Color-fill-brand-color, #FF6F0F)' : '2px solid transparent',
                transition: 'all 0.2s'
              }}
              onClick={() => setBatchDeleteType('delete')}
              >
                <input
                  type="radio"
                  name="batchDeleteType"
                  value="delete"
                  checked={batchDeleteType === 'delete'}
                  onChange={() => setBatchDeleteType('delete')}
                  style={{
                    marginTop: '2px',
                    cursor: 'pointer'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    lineHeight: '21px',
                    color: 'var(--Color-text-normal, #161616)',
                    marginBottom: '4px'
                  }}>
                    완전 삭제 (계정 삭제)
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '400',
                    lineHeight: '18px',
                    color: 'var(--Color-text-assistive, #989898)'
                  }}>
                    학생의 모든 데이터와 계정이 영구적으로 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
                  </div>
                </div>
              </label>
            </div>

            {/* 경고 메시지 */}
            {batchDeleteType === 'delete' && (
              <div style={{
                padding: '12px 16px',
                background: '#FFEBEE',
                borderRadius: '8px',
                border: '1px solid #EF5350'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '12px',
                  fontWeight: '400',
                  lineHeight: '18px',
                  color: '#C62828'
                }}>
                  ⚠️ 이 작업은 되돌릴 수 없습니다. 선택한 {selectedStudents.size}명의 모든 데이터와 계정이 영구적으로 삭제됩니다.
                </p>
              </div>
            )}

            {/* 버튼 영역 */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}>
              <Button
                variant="cancel"
                onClick={handleCancelBatchDelete}
                width="100px"
                height="40px"
                disabled={batchDeleting}
              >
                취소
              </Button>
              <button
                type="button"
                onClick={handleConfirmBatchDelete}
                disabled={batchDeleting}
                style={{
                  width: '140px',
                  height: '40px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: batchDeleting ? '#F5F5F5' : '#E57373',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  lineHeight: '21px',
                  cursor: batchDeleting ? 'not-allowed' : 'pointer',
                  opacity: batchDeleting ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  fontFamily: 'Pretendard',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!batchDeleting) {
                    e.currentTarget.style.backgroundColor = '#D32F2F';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!batchDeleting) {
                    e.currentTarget.style.backgroundColor = '#E57373';
                  }
                }}
              >
                {batchDeleting ? '처리 중...' : batchDeleteType === 'delete' ? '완전 삭제' : '제거하기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassManagement; 